import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import Euro from "@mui/icons-material/Euro";
import Dollar from "@mui/icons-material/AttachMoney";

export default function PriceChangeModal({ 
  open, 
  onClose, 
  selectedRows, 
  rows, 
  onPriceChange,
  currencySymbol,
  currency
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [priceValue, setPriceValue] = useState('');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 0, isSame: true });
  const [localRows, setLocalRows] = useState([]);

  useEffect(() => {
    if (selectedRows.length > 0) {
      const selectedRowsData = rows.filter(row => selectedRows.includes(row.id));
      setLocalRows(selectedRowsData);

      const selectedPrices = selectedRowsData
        .map(row => row.price)
        .filter(price => price !== 'Free')
        .map(price => parseFloat(price) || 0);

      if (selectedPrices.length > 0) {
        const min = Math.min(...selectedPrices);
        const max = Math.max(...selectedPrices);
        const isSame = min === max;

        setPriceRange({ min, max, isSame });
        if (isSame) {
          setPriceValue(min.toString());
          setIsEditing(true);
        } else {
          setIsEditing(false);
          setPriceValue(`${currencySymbol}${min}-${currencySymbol}${max}`);
        }
      } else {
        setPriceRange({ min: 0, max: 0, isSame: true });
        setPriceValue('0');
        setIsEditing(true);
      }
    }
  }, [selectedRows, rows, currencySymbol]);

  const columns = [
    { field: 'item', headerName: 'Track', flex: 1 },
    { 
      field: 'price', 
      headerName: `Price (${currencySymbol})`, 
      width: 150,
      align: 'left',
      headerAlign: 'left',
    },
  ];

  const handleEditClick = () => {
    setIsEditing(true);
    setPriceValue('0');
    // Update all rows with the new price when edit is clicked
    setLocalRows(prevRows => 
      prevRows.map(row => ({
        ...row,
        price: '0'
      }))
    );
  };

  const handlePriceChange = (event) => {
    const value = event.target.value;
    if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
      setPriceValue(value);
      // Update all rows with the new price
      setLocalRows(prevRows => 
        prevRows.map(row => ({
          ...row,
          price: value === '' ? '0' : value
        }))
      );
    }
  };

  const handleConfirm = () => {
    // newPrice will be a string (e.g., "5", "0", or "" if user cleared it).
    // PlaylistModal's handlePriceChange is equipped to handle this string.
    const newPrice = priceValue; 
    onPriceChange(selectedRows, newPrice);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: "#d8c8f4",
          color: "#4b445b",
        },
      }}
    >
      <DialogTitle>Change Price for Selected Songs</DialogTitle>
      <DialogContent>
        <Box sx={{ height: 400, width: '100%', mb: 2 }}>
          <DataGrid
            rows={localRows}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
            sx={{
              borderColor: "primary.main",
              "& .MuiDataGrid-columnHeaders": {
                borderColor: "primary.main",
                backgroundColor: "#f5f5f5",
              },
              "& .MuiDataGrid-cell": {
                borderColor: "primary.main",
              },
            }}
          />
        </Box>
        <TextField
          label="New Price"
          variant="outlined"
          fullWidth
          value={priceValue}
          onChange={handlePriceChange}
          disabled={!isEditing}
          type="number"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {currency === "EUR" && (
                  <Euro sx={{ width: "20px", height: "20px" }} />
                )}
                {currency === "USD" && (
                  <Dollar sx={{ width: "20px", height: "20px" }} />
                )}
              </InputAdornment>
            ),
            endAdornment: !priceRange.isSame && !isEditing ? (
              <InputAdornment position="end">
                <IconButton onClick={handleEditClick}>
                  <EditIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          inputProps={{
            min: 0,
            step: "0.01"
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Cancel</Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          disabled={!isEditing}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}